/**
 * 用户充值页面
 * 用户可以使用兑换码充值 token
 */

import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/auth";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Coins, Gift } from "lucide-react";

interface QuotaInfo {
  quota: number;
  used_quota: number;
  request_count: number;
}

export function TopupPage() {
  const { user } = useAuthStore();
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  // 加载额度信息
  const loadQuota = async () => {
    try {
      setLoading(true);
      const authStore = localStorage.getItem("auth-storage");
      if (!authStore) {
        throw new Error("未登录");
      }

      const { state } = JSON.parse(authStore);
      const token = state?.accessToken;
      if (!token) {
        throw new Error("未登录");
      }

      const response = await fetch("/api/user/quota", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("获取额度信息失败");
      }

      const result = await response.json();
      if (result.success) {
        setQuotaInfo(result.data);
      } else {
        toast.error(result.message || "获取额度信息失败");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "获取额度信息失败");
    } finally {
      setLoading(false);
    }
  };

  // 使用兑换码充值
  const handleTopup = async () => {
    if (!redeemCode.trim()) {
      toast.error("请输入兑换码");
      return;
    }

    try {
      setRedeeming(true);

      const authStore = localStorage.getItem("auth-storage");
      if (!authStore) {
        throw new Error("未登录");
      }

      const { state } = JSON.parse(authStore);
      const token = state?.accessToken;
      if (!token) {
        throw new Error("未登录");
      }

      const response = await fetch("/api/user/topup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: redeemCode.trim() }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "充值失败");
      }

      const result = await response.json();
      if (result.success) {
        toast.success(
          `充值成功！获得 ${result.data.quotaAdded} token，当前余额: ${result.data.newQuota}`
        );
        setRedeemCode("");
        // 重新加载额度信息
        loadQuota();
      } else {
        toast.error(result.message || "充值失败");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "充值失败");
    } finally {
      setRedeeming(false);
    }
  };

  // 格式化数字
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  useEffect(() => {
    loadQuota();
  }, []);

  if (!user) {
    return (
      <LayoutWithSidebar>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-gray-500">请先登录</p>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar>
      <div className="container mx-auto p-6 space-y-6">
        {/* 标题 */}
        <div>
          <h1 className="text-3xl font-bold">Token 充值</h1>
          <p className="text-muted-foreground mt-1">
            使用兑换码为您的账户充值 Token
          </p>
        </div>

        {/* 额度信息卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                当前余额
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-yellow-600" />
                <div className="text-2xl font-bold text-yellow-600">
                  {loading ? "..." : formatNumber(quotaInfo?.quota || 0)}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">可用 Token</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                已使用
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">
                {loading ? "..." : formatNumber(quotaInfo?.used_quota || 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-2">已消耗 Token</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                请求次数
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {loading ? "..." : formatNumber(quotaInfo?.request_count || 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-2">API 调用次数</p>
            </CardContent>
          </Card>
        </div>

        {/* 兑换码充值 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-purple-600" />
              兑换码充值
            </CardTitle>
            <CardDescription>
              输入兑换码为您的账户充值 Token
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="redeem-code">兑换码</Label>
                <Input
                  id="redeem-code"
                  placeholder="请输入兑换码"
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleTopup();
                    }
                  }}
                  disabled={redeeming}
                />
                <p className="text-xs text-muted-foreground">
                  兑换码格式：sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                </p>
              </div>

              <Button
                onClick={handleTopup}
                disabled={redeeming || !redeemCode.trim()}
                className="w-full"
              >
                {redeeming ? "充值中..." : "立即充值"}
              </Button>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  💡 充值说明
                </p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• 新用户注册即送 5000 Token</li>
                  <li>• 使用兑换码可以为账户充值更多 Token</li>
                  <li>• Token 用于支付 API 调用费用</li>
                  <li>• 请从管理员处获取兑换码</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 使用记录 */}
        <Card>
          <CardHeader>
            <CardTitle>使用说明</CardTitle>
            <CardDescription>关于 Token 计费的说明</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-medium text-gray-900 mb-1">什么是 Token？</p>
                <p className="text-gray-600">
                  Token 是 API 调用的计费单位，每次调用 AI
                  接口都会消耗相应数量的 Token。
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">如何计费？</p>
                <p className="text-gray-600">
                  不同模型和不同类型的操作消耗的 Token
                  数量不同，具体请参考 API 文档。
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">如何获取更多 Token？</p>
                <p className="text-gray-600">
                  您可以向管理员申请兑换码，或关注活动获取免费 Token。
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </LayoutWithSidebar>
  );
}
